import React, { useCallback, useRef, useState } from "react";
import {
  GoogleMap,
  Marker,
  useJsApiLoader,
  InfoWindow,
} from "@react-google-maps/api";
import { Box, Button, Stack, Typography } from "@mui/material";
import { IOperatorTracking } from "src/types/employees";
import PersonIcon from "@mui/icons-material/Person";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import OperatorPathMap from "../operator-path";

interface Location {
  locationId: string;
  locationName: string;
  locationLatitude: number;
  locationLongitude: number;
}

interface IProps {
  list?: IOperatorTracking[];
}

const containerStyle = { height: "100%", width: "100%" };

const AllMap: React.FC<IProps> = ({ list }) => {
  const [activeOperator, setActiveOperator] =
    useState<IOperatorTracking | null>(null);
  const [open, setOpen] = useState(false);

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: "AIzaSyBjGZRBq1seDBmbq62CMyV2VkpRrOwNh_Q",
  });

  const mapRef = useRef<google.maps.Map | null>(null);

  const onLoad = useCallback(
    (map: google.maps.Map) => {
      mapRef.current = map;
      if (!list || list.length === 0) return;

      const bounds = new window.google.maps.LatLngBounds();
      list?.forEach((item) => {
        const lat = item.trackingList[0]?.latitude;
        const lng = item.trackingList[0]?.longitude;

        const isValidCoord =
          typeof lat === "number" &&
          typeof lng === "number" &&
          !isNaN(lat) &&
          !isNaN(lng) &&
          lat >= -90 &&
          lat <= 90 &&
          lng >= -180 &&
          lng <= 180;

        if (isValidCoord) {
          bounds.extend({
            lat: item.trackingList[0]?.latitude,
            lng: item.trackingList[0]?.longitude,
          });
        }
      });

      map.fitBounds(bounds); // auto-zoom & center
    },
    [list],
  );

  const onUnmount = useCallback(() => {
    mapRef.current = null;
  }, []);

  const handleOpen = (id: string) => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  console.log({ list });

  return isLoaded ? (
    <Box borderRadius={2} overflow="hidden" height={"100%"}>
      <GoogleMap
        onClick={() => setActiveOperator(null)}
        mapContainerStyle={containerStyle}
        onLoad={onLoad}
        onUnmount={onUnmount}
        options={{
          mapTypeControl: true,
          streetViewControl: false,
          fullscreenControl: false,
        }}
      >
        {list?.map((operator) => {
          const lat = operator.trackingList?.[0]?.latitude;
          const lng = operator.trackingList?.[0]?.longitude;

          const isValidCoord =
            typeof lat === "number" &&
            typeof lng === "number" &&
            !isNaN(lat) &&
            !isNaN(lng) &&
            lat >= -90 &&
            lat <= 90 &&
            lng >= -180 &&
            lng <= 180;

          if (!isValidCoord) return null;

          return (
            <>
              <Marker
                key={operator.id}
                position={{ lat, lng }}
                label={{
                  text: " ",
                  color: "#464646",
                  fontSize: "12px",
                  fontWeight: "500",
                  className: "marker-label-custom",
                }}
                onClick={() => setActiveOperator(operator)}
              />
              {activeOperator && activeOperator.id === operator.id && (
                <InfoWindow
                  position={{
                    lat: activeOperator.trackingList[0].latitude,
                    lng: activeOperator.trackingList[0].longitude,
                  }}
                  onCloseClick={() => setActiveOperator(null)}
                >
                  <Box
                    border={"1px solid #242424"}
                    padding={1}
                    display={"flex"}
                    flexDirection={"column"}
                    gap={0.5}
                    borderRadius={"4px"}
                    bgcolor={"#000"}
                    minWidth={"164px"}
                    maxWidth={"164px"}
                  >
                    <Stack direction={"row"} alignItems={"center"} gap={1}>
                      <PersonIcon fontSize="small" />
                      <Typography fontSize={"12px"}>
                        {operator.operatorName}
                      </Typography>
                    </Stack>
                    <Stack direction={"row"} alignItems={"center"} gap={1}>
                      <LocalPhoneIcon fontSize="small" />
                      <Typography fontSize={"12px"}>
                        {operator.operatorPhoneNumber ?? "_"}
                      </Typography>
                    </Stack>
                    <Stack direction={"row"} alignItems={"center"} gap={1}>
                      <LocationOnIcon fontSize="small" />
                      <Typography fontSize={"12px"}>
                        {operator.supportMode
                          ? "Support Mode"
                          : operator.locationName}
                      </Typography>
                    </Stack>
                    <Stack direction={"row"} justifyContent={"flex-end"}>
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => handleOpen(operator.id)}
                      >
                        Track
                      </Button>
                    </Stack>
                  </Box>
                </InfoWindow>
              )}
            </>
          );
        })}
      </GoogleMap>

      {open && activeOperator && (
        <OperatorPathMap
          open={open}
          onClose={handleClose}
          id={activeOperator.id}
        />
      )}
    </Box>
  ) : null;
};

export default AllMap;
